import React from 'react'
import { useNavigate } from 'react-router-dom'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  PixelModal,
  PixelPageContainer,
  PixelTable,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import queryString from 'query-string'
import { getWarehouseInventory } from 'utils/api-routes-inventory'
import { exportInventory } from 'utils/api-routes.util'

import InventoryUploadModal from './components/InventoryUploadModal'
export interface IInventory {
  product_id: string
  product_name: string
  product_sku: string
  total_stock_count: string
  physical_count: string
  virtual_count: string
  wh_id_1: string
  wh_name_1: string
  wh_id_1_count: string
  wh_id_1_virtual_count: string
  wh_id_1_physical_count: string
}

export interface IFilters {
  page: string | number | (string | null)[]
  warehouse_id: string | number | (string | null)[]
  search: string | (string | null)[]
  page_size: string | number | (string | null)[]
  sortKey: string | (string | null)[]
  sortOrder: string | (string | null)[]
}

const breadcrumbs = [
  {
    name: 'Manage Inventory',
    active: true,
  },
]
const columns = [
  {
    dataKey: 'product_sku',
    name: 'SKU',
    width: '16%',
    sortable: true,
    selector: (row) =>
      row.product_sku ? (
        <DcDiv padding="10px">
          <PixelText copyToClipboard multiLine>
            {row.product_sku}
          </PixelText>
        </DcDiv>
      ) : (
        '--'
      ),
  },
  {
    dataKey: 'product_name',
    name: (
      <DcDiv padding="10px">
        <PixelText>Product Name</PixelText>
      </DcDiv>
    ),
    width: '25%',
    sortable: true,
    selector: (row) => <PixelText multiLine>{row.product_name}</PixelText>,
  },
  {
    dataKey: 'total_stock_count',
    name: <PixelText>Total Stock</PixelText>,
    width: '15%',
    sortable: true,
    selector: (row) => row.total_stock_count || '--',
  },
]
const Inventory = () => {
  const navigate = useNavigate()
  const { state } = React.useContext(OrdersContext)
  const [inventory, setInventory] = React.useState<IInventory[]>([])
  const [tableLoading, setTableLoader] = React.useState(false)
  const [warehousesList, setWarehousesList] = React.useState<any[]>([])
  const [totalRows, setTotalRows] = React.useState(0)
  const [uploadInventoryModal, setUploadInventoryModal] = React.useState(false)
  const [columnState, setColumnState] = React.useState<any[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState<IFilters>({
    page: p.page || 0,
    warehouse_id: p.warehouse_id ? p.warehouse_id : 'all',
    search: p.search ? p.search : '',
    page_size: p.page_size || 50,
    sortKey: p.sortKey || '',
    sortOrder: p.sortOrder || '',
  })
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)

  // Ref to track if this is the initial load
  const isInitialLoad = React.useRef(true)

  // Ref to prevent multiple simultaneous API calls
  const isFetching = React.useRef(false)

  // Ref to track the last successful API call to prevent unnecessary re-fetches
  const lastSuccessfulCall = React.useRef<string>('')

  const handleSort = async (column, sortDirection) => {
    // Only allow sorting by fields that the API supports
    const sortableKeys = ['product_sku', 'product_name', 'total_stock_count'] // Check if the column is sortable
    if (!sortableKeys.includes(column.dataKey)) {
      return
    }

    const newFilters = {
      ...filters,
      sortKey: column.dataKey,
      sortOrder: sortDirection.toUpperCase(),
    }

    setFilters(newFilters)
  }
  const readData = (filters) => {
    // Prevent multiple simultaneous API calls
    if (isFetching.current) {
      return
    }

    isFetching.current = true
    setTableLoader(true)

    getWarehouseInventory(filters).subscribe({
      next: (res: any) => {
        // Clear any previous errors on success
        setError(null)

        // Track this successful API call to prevent duplicates
        const requestKey = JSON.stringify({
          page: filters.page,
          page_size: filters.page_size,
          warehouse_id: filters.warehouse_id,
          search: filters.search,
          sortKey: filters.sortKey,
          sortOrder: filters.sortOrder,
        })
        lastSuccessfulCall.current = requestKey

        setInventory(res.response?.data)
        const warehouse_columns = res.response?.columns.map((column) => {
          return {
            ...column,
            sortable: true, // Ensure all warehouse columns are sortable
            selector: (row) => {
              const selectorFn = new Function(
                'row',
                `return ${column.selector}`,
              )
              return selectorFn(row)
            },
            name: <PixelText>{column.name}</PixelText>,
          }
        })
        setColumnState([...columns, ...warehouse_columns])
        setTotalRows(res.response._meta?.total_product_count)

        // Remove server sync logic that causes infinite loops
        // The server response is authoritative, no need to sync back to filters

        setTableLoader(false)
        isFetching.current = false
      },
      error: (error) => {
        console.error('Inventory API failed:', error)
        // Stop all loaders on error
        setTableLoader(false)
        isFetching.current = false
        // Clear inventory data on error
        setInventory([])
        setColumnState([])
        setTotalRows(0)
        // Set user-friendly error message
        setError(
          'Failed to load inventory data. The server may be experiencing issues. Please try again later.',
        )
        // You can add a toast notification here if you have a toast system
        // showToast('error', 'Failed to load inventory data. Please try again.')
      },
    })
  }

  const exportReport = (exportFilters: IFilters) => {
    exportFilters.page_size = 10000
    exportInventory(filters)
  }

  // Combined effect for navigation and data fetching to prevent double triggers
  React.useEffect(() => {
    // Skip the first render to prevent double fetching
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      return
    }

    // Skip if we're already fetching to prevent concurrent requests
    if (isFetching.current) {
      return
    }

    // Create a unique key for this request to prevent duplicate calls
    const requestKey = JSON.stringify({
      page: filters.page,
      page_size: filters.page_size,
      warehouse_id: filters.warehouse_id,
      search: filters.search,
      sortKey: filters.sortKey,
      sortOrder: filters.sortOrder,
    })

    // Skip if this exact request was already made successfully
    if (lastSuccessfulCall.current === requestKey) {
      return
    }

    // Add a small delay to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      // Double-check flags before proceeding
      if (isFetching.current) {
        return
      }

      // Update URL and fetch data together to prevent double triggers
      navigate({
        pathname: '/inventory',
        search: queryString.stringify(filters),
      })

      readData(filters)
    }, 100)

    // Cleanup function to prevent stale requests
    return () => {
      clearTimeout(timeoutId)
      isFetching.current = false
    }
  }, [
    filters.page,
    filters.page_size,
    filters.warehouse_id,
    filters.search,
    filters.sortKey,
    filters.sortOrder,
  ])

  // Initial data fetch on mount - only run once
  React.useEffect(() => {
    if (!isInitialLoad.current) {
      return
    }
    readData(filters)
  }, [])

  // Ensure default page size is set on mount
  React.useEffect(() => {
    if (!filters.page_size || filters.page_size !== 50) {
      setFilters((prev) => ({ ...prev, page_size: 50 }))
    }
  }, [])

  React.useEffect(() => {
    if (state?.warehouses) {
      setWarehousesList(state.warehouses)
    }
  }, [state])

  const conditionalRowStyles = [
    {
      when: (row) => row.is_active === '1',
      style: {
        backgroundColor: '#f5f5f5',
      },
    },
  ]
  const onRowClicked = (row: any) => {
    navigate(`/inventory/details/${row.product_id}`)
  }

  const handlePageChange = (page) => {
    if (filters.page === page - 1) return
    setFilters({
      ...filters,
      page: page - 1,
    })
  }
  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
      page: 0, // Reset to first page when page size changes
    })
  }

  return (
    <>
      <DcFlexBox
        height="60px"
        fontSize="16px"
        style={{
          fontSize: '16px',
          padding: '8px 20px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 0px"
        >
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />

          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer style={{ padding: '0px' }}>
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: ' 0px' }}>
            <DcFlexBox
              padding="5px 20px"
              justifyContent="space-between"
              alignItems="center"
              height="auto"
            >
              <DcFlexBox
                justifyContent="flex-end"
                align-items="center"
                alignContent="center"
                gap="5px"
                width="60%"
                padding="0px"
              >
                <DcDropDown
                  dropdownSize="sm"
                  required
                  placeholder={'Select Warehouse'}
                  options={[
                    { name: 'Select Warehouse', wh_id: 'all' },
                    ...warehousesList,
                  ].map((data: any) => {
                    return {
                      label: data.name,
                      value: data.wh_id,
                    }
                  })}
                  value={filters.warehouse_id}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      warehouse_id: e.target.value,
                    })
                  }}
                />
                <DcInput
                  size="sm"
                  showsearchicon={0}
                  placeholder="Search"
                  name="search"
                  onChange={(e) => {
                    setResetPaginationToggle(e.target.value.length > 0)
                    setFilters({
                      ...filters,
                      search: e.target.value,
                    })
                  }}
                  value={filters.search}
                />
              </DcFlexBox>
              <DcFlexBox justifyContent="flex-end" gap="5px">
                <DcButton
                  onClick={() => {
                    navigate('/inventory/edit')
                  }}
                  size="sm"
                >
                  Adjust Inventory
                </DcButton>
                <DcButton
                  variant="outline-info"
                  onClick={() => {
                    setUploadInventoryModal(true)
                  }}
                  size="sm"
                >
                  Upload Inventory
                </DcButton>
                <DcButton
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    navigate('/inventory/uploads')
                  }}
                >
                  Upload History
                </DcButton>
                <DcButton
                  size="sm"
                  variant="outline-warning"
                  onClick={() => {
                    navigate('/inventory/lifecycle')
                  }}
                >
                  Inventory Lifecycle
                </DcButton>

                <DcButton
                  onClick={() => {
                    exportReport(filters)
                  }}
                  variant="outline-success"
                  size="sm"
                >
                  Export
                </DcButton>
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent padding="10px">
            {/* Error Display */}
            {error && (
              <DcFlexBox
                style={{
                  backgroundColor: '#fee',
                  border: '1px solid #fcc',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px',
                  color: '#c33',
                }}
                justifyContent="space-between"
                alignItems="center"
              >
                <PixelText style={{ color: '#c33', margin: 0 }}>
                  {error}
                </PixelText>
                <DcButton
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    setError(null)
                    readData(filters)
                  }}
                >
                  Retry
                </DcButton>
              </DcFlexBox>
            )}

            <PixelTable
              key={`inventory-table-${filters.page_size}`}
              pagination={true}
              paginationServer
              columns={(() => {
                return columnState
              })()}
              data={inventory || []}
              highlightOnHover={true}
              fixedHeader
              onSort={(column, sortDirection) => {
                handleSort(column, sortDirection)
              }}
              sortServer
              fixedHeaderScrollHeight="calc(50vh - 216px)"
              pointerOnHover={true}
              paginationPerPage={filters.page_size}
              progressPending={tableLoading}
              onChangeRowsPerPage={handlePerRowsChange}
              conditionalRowStyles={conditionalRowStyles}
              onRowClicked={onRowClicked}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              onChangePage={handlePageChange}
              paginationTotalRows={totalRows}
              paginationResetDefaultPage={resetPaginationToggle}
              paginationDefaultPage={Number(filters.page) + 1}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <PixelModal
        size={{
          width: '600px !important',
          maxWidth: '100% !important',
        }}
        title={'Bulk stock update from file'}
        show={uploadInventoryModal}
        handleClose={() => setUploadInventoryModal(false)}
      >
        {uploadInventoryModal && (
          <InventoryUploadModal
            warehousesList={warehousesList}
            setUploadInventoryModal={setUploadInventoryModal}
            handleClose={(data?) => {
              if (data === 'success') {
                readData(filters)
              }
              setUploadInventoryModal(false)
            }}
          />
        )}
      </PixelModal>
    </>
  )
}

export default Inventory
